import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import CategoryTabs from '@/components/feature/CategoryTabs';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'all') return articles;
    return articles.filter((a) => a.category === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 51吃瓜网` : '全部文章 - 51吃瓜网',
    description: `51吃瓜网${currentCategory ? currentCategory.name : '全部'}分类下的网红资讯与八卦内容`,
    url: `https://51chigua.example.com/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://51chigua.example.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      {/* Category Header */}
      <section className="w-full px-4 md:px-6 pt-28 pb-10">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '全部' },
            ]}
          />

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl">
              <i className={`${currentCategory?.icon || 'ri-global-line'} text-xl text-primary-600`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {currentCategory?.name || '全部文章'}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredArticles.length} 篇文章
              </p>
            </div>
          </div>

          <CategoryTabs
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>
      </section>

      {/* Articles Grid */}
      <section className="w-full px-4 md:px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}